import { useState, useCallback } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Users } from 'lucide-react';
import { clientsApi } from '@/api/clients';
import type { Client } from '@/types';
import DataTable from '@/components/DataTable';
import SearchInput from '@/components/SearchInput';
import Pagination from '@/components/Pagination';
import EmptyState from '@/components/EmptyState';
import { formatDate, getStatusColor, formatStatus } from '@/utils/formatters';

export function Clients() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const limit = 10;

  const { data: response, isLoading, error } = useQuery({
    queryKey: ['clients', page, search],
    queryFn: () => clientsApi.getAll({ page, limit, search }),
    placeholderData: keepPreviousData,
  });

  const clients = response?.data?.data || [];
  const totalPages = response?.data?.totalPages || 1;

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const columns = [
    { key: 'name', label: 'Nombre', sortable: true },
    { key: 'company', label: 'Empresa', sortable: true, render: (item: Client) => item.company || '-' },
    { key: 'email', label: 'Email', render: (item: Client) => item.email || '-' },
    { key: 'phone', label: 'Teléfono', render: (item: Client) => item.phone || '-' },
    {
      key: 'isActive',
      label: 'Estado',
      sortable: true,
      render: (item: Client) => (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(item.active ? 'active' : 'inactive')}`}>
          {item.active ? 'Activo' : 'Inactivo'}
        </span>
      ),
    },
    { key: 'createdAt', label: 'Creado', sortable: true, render: (item: Client) => formatDate(item.createdAt) },
  ];

  const errorMessage = error
    ? typeof error === 'string'
      ? error
      : 'Error al cargar los clientes'
    : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-surface-900">Clientes</h1>
        <button
          type="button"
          onClick={() => navigate('/clients/new')}
          className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Nuevo Cliente
        </button>
      </div>

      <div className="w-full max-w-sm">
        <SearchInput
          value={search}
          onChange={handleSearch}
          placeholder="Buscar cliente..."
        />
      </div>

      <DataTable<Client>
        columns={columns}
        data={clients}
        loading={isLoading}
        error={errorMessage}
        emptyMessage="No hay clientes registrados"
        onRowClick={(item) => navigate(`/clients/${item.id}`)}
      />

      {!isLoading && clients.length > 0 && (
        <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
      )}
    </div>
  );
}

export default Clients;
